<template>
  <section class="mt-5 mb-5">
    <div class="container">
      <!-- Header -->
      <div class="row justify-content-center">
        <div class="col-md-8">
          <div class="page-bar py-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                  <i class="fas fa-chevron-left"></i>
                </BackButton>
                <div class="page-title flex-grow-1">Send Enquiry</div>
              </div>
            </div>
          </div>

          <!-- Main card -->
          <div class="card shadow-sm mt-3">
            <div class="card-body">

              <!-- Helper text -->
              <p class="text-muted mb-3">
                Choose your categories and provide a description to ensure the right businesses can reach out to you.
              </p>

              <!-- Categories -->
              <div class="mb-3">
                <label class="font-weight-bold d-block mb-2">Category</label>

                <!-- Loading categories -->
                <div v-if="loadingCats" class="py-3 text-center">
                  <div class="spinner-border text-primary"></div>
                </div>

                <!-- Categories chips -->
                <div v-else>
                  <div v-if="categories.length" class="chips-wrap">
                    <button
                      v-for="c in categories"
                      :key="c.id"
                      type="button"
                      class="chip-btn"
                      :class="{ active: selected.has(c.id) }"
                      @click="toggle(c.id)"
                    >
                      {{ c.name }}
                    </button>
                  </div>
                  <div v-else class="text-muted small">
                    No categories found near the event location.
                  </div>
                </div>
              </div>

              <!-- Enquiry details -->
              <div class="mb-3">
                <label for="enquiryDetails" class="font-weight-bold d-block mb-2">Enquiry Details</label>
                <textarea
                  id="enquiryDetails"
                  class="form-control"
                  rows="5"
                  placeholder="Describe what you’re looking for, your budget range, preferred dates, etc."
                  v-model.trim="details"
                ></textarea>
                <div class="invalid small mt-1" v-if="errorMsg">{{ errorMsg }}</div>
              </div>

              <!-- Submit -->
              <div class="mb-3">
                <button
                  class="btn btn-primary btn-block"
                  :disabled="sending || selected.size === 0 || !details"
                  @click="submit"
                >
                  <span v-if="!sending">Send Enquiry</span>
                  <span v-else><span class="spinner-border spinner-border-sm mr-1"></span> Sending…</span>
                </button>
              </div>

              <!-- How it works -->
              <div class="mt-4">
                <h6 class="font-weight-bold mb-2">How it works:</h6>
                <ol class="text-muted pl-3 small mb-0">
                  <li>We’ll share your contact information (email and phone number) with the business.</li>
                  <li>They’ll reach out to you via phone or email.</li>
                  <li>Then, you can select the option that best fits your needs.</li>
                </ol>
              </div>

            </div>
          </div>

          <!-- Error state (page level) -->
          <div v-if="loadError" class="alert alert-warning mt-3">
            {{ loadError }}
            <button class="btn btn-link p-0 ml-2" @click="reload">Retry</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import BackButton from '@/components/common/BackButton.vue'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()

/** ---------- Route / Event Id ---------- */
const deriveEventId = (r) => {
  const cand = [
    r.params?.slug, r.params?.eventid, r.params?.id, r.params?.eventId,
    r.query?.slug,  r.query?.eventid, r.query?.id,  r.query?.eventId
  ].find(v => v != null && String(v).trim() !== '')
  if (cand) return String(cand)
  const last = (r.path || r.fullPath || '').split('?')[0].split('/').filter(Boolean).pop()
  return last && /^\d+$/.test(last) ? last : null
}
const eventId = ref(deriveEventId(route))
watch(() => route.fullPath, () => { eventId.value = deriveEventId(route) })

/** ---------- State ---------- */
const loadingCats = ref(true)
const sending = ref(false)
const loadError = ref('')
const errorMsg = ref('')

const details = ref('')
const selected = ref(new Set())                    // selected category ids
const categories = ref([])                         // [{id, name}]
const eventLoc = reactive({ lat: null, lng: null })// from event

/** ---------- Nav ---------- */
const goBack = () => {
  if (window.history.length > 1) router.back()
  else router.push('/host')
}

/** ---------- Helpers ---------- */
const toggle = (id) => {
  const s = new Set(selected.value)
  if (s.has(id)) s.delete(id)
  else s.add(id)
  selected.value = s
}

/** Build unique categories from vendor search results */
// function buildCategoriesFromVendors(vendors = []) {
//   const map = new Map() // id -> name
//   for (const v of vendors) {
//     // Backend fields we’ve seen:
//     //   - contextual_category  (CSV of ids)
//     //   - context_categories   (CSV of names)
//     const ids = String(v.contextual_category || v.contextual_categories_ids || '')
//       .split(',').map(s => s.trim()).filter(Boolean)
//     const names = String(v.context_categories || v.contextual_categories || '')
//       .split(',').map(s => s.trim()).filter(Boolean)

//     const len = Math.max(ids.length, names.length)
//     for (let i = 0; i < len; i++) {
//       const id = ids[i] || (names[i] ? names[i].toLowerCase().replace(/\s+/g,'-') : `cat-${i}`)
//       const name = names[i] || ids[i] || 'Category'
//       if (!map.has(id)) map.set(id, name)
//     }
//   }
//   categories.value = Array.from(map, ([id, name]) => ({ id, name }))
// }

/** Load event (to get lat/lng) then fetch vendors to derive categories */
async function loadCategories() {
  try {
    loadingCats.value = true
    loadError.value = ''

    // 1) get event details (lat/lng)
    const fd1 = new FormData()
    fd1.append('userid', session.userId)
    fd1.append('eventid', eventId.value)
    const res1 = await api.getInvitationDetail(fd1)
    const payload1 = res1?.data ?? res1
    const info = Array.isArray(payload1?.eventsinfo) ? payload1.eventsinfo[0] : {}
    const lat = Number(info?.lat ?? info?.latitude)
    const lng = Number(info?.lng ?? info?.longitude)
    if (!isFinite(lat) || !isFinite(lng)) {
      throw new Error('Event location not found.')
    }
    eventLoc.lat = lat
    eventLoc.lng = lng

    // 2) get vendors near event to derive categories
    const fd2 = new FormData()
    fd2.append('lat', String(lat))
    fd2.append('long', String(lng)) 
    const res2 = await api.getEnquiryCategories(fd2)
    const payload2 = res2?.data ?? res2
    categories.value = Array.isArray(payload2?.list) ? payload2.list : []
 
  } catch (e) {
    console.error('loadCategories error:', e)
    loadError.value = e?.message || 'Failed to load categories.'
    categories.value = []
  } finally {
    loadingCats.value = false
  }
}

const reload = () => loadCategories()

/** ---------- Submit ---------- */
async function submit() {
  errorMsg.value = ''
  if (selected.value.size === 0) {
    errorMsg.value = 'Please select at least one category.'
    return
  }
  if (!details.value.trim()) {
    errorMsg.value = 'Please provide enquiry details.'
    return
  }

  try {
    sending.value = true
    const ids = Array.from(selected.value)

    const fd = new FormData()
    fd.append('userid', session.userId)
    fd.append('eventid', eventId.value)
    fd.append('categories', ids.join(','))  // CSV of category ids
    fd.append('description', details.value)
    // Include coordinates for backend convenience (optional)
    if (eventLoc.lat != null) fd.append('lat', String(eventLoc.lat))
    if (eventLoc.lng != null) fd.append('long', String(eventLoc.lng))

    const res = await api.sendEnquiry(fd)
    const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
    if (!ok) {
      const msg = res?.data?.message || res?.message || 'Failed to send enquiry.'
      throw new Error(msg)
    }

    // reset UI and route back
    selected.value = new Set()
    details.value = ''
    alert('Enquiry sent successfully!')
    goBack()
  } catch (e) {
    console.error('sendEnquiry error:', e)
    alert(e?.message || 'Failed to send enquiry.')
  } finally {
    sending.value = false
  }
}

/** ---------- Boot ---------- */
onMounted(async () => {
  if (!session.userId) await session.loadUser()
  await loadCategories()
})
</script>

<style scoped>
.page-bar{
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.page-title{ font-weight: 600; font-size: 1.05rem; }

/* Chips */
.chips-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem .5rem;
}
.chip-btn{
  border: 1px solid #e1e1e1;
  background: #fff;
  padding: .35rem .75rem;
  border-radius: 999px;
  font-size: .9rem;
  line-height: 1;
  cursor: pointer;
}
.chip-btn.active{
  border-color: #0d6efd;
  color: #0d6efd;
  background: #eef5ff;
}

/* Validation note */
.invalid{ color:#d9534f; }

/* Button full width on mobile */
.btn-block{ width:100%; }
</style>
